import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { missionService, userService, matrixService } from '../services/api';
import { useLocalizedContent, getLocalizedContent } from '../utils/language';

function Dashboard() {
  const { user, isInstructor } = useAuth();
  const { t, i18n } = useTranslation();
  const getLocalizedText = useLocalizedContent();
  const [stats, setStats] = useState(null);
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [isInstructor]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch missions
      const missionsRes = await missionService.getMissions();
      setMissions(missionsRes.data || []);

      // Fetch stats based on role - only real data
      if (isInstructor) {
        const usersRes = await userService.getAllUsers();
        const pendingUsersRes = await userService.getPendingUsers();

        setStats({
          totalMissions: missionsRes.data?.length || 0,
          activeMissions: missionsRes.data?.filter(m => m.is_active).length || 0,
          totalStudents: usersRes.data?.filter(u => u.role === 'analyst').length || 0,
          pendingApprovals: pendingUsersRes.data?.length || 0
        });
      } else {
        const matricesRes = await matrixService.getMyMatrices();
        const assignedMissions = missionsRes.data?.filter(m => !m.archived) || [];
        // El camp is_submitted no ha existit mai: les matrius porten `status`.
        // Per això «completades» sempre marcava 0 i «en curs» comptava-ho tot,
        // fins i tot les entregues ja lliurades.
        const matrices = matricesRes.data || [];
        const acabades = matrices.filter(
          m => m.status === 'submitted' || m.status === 'time_expired'
        );

        setStats({
          assignedMissions: assignedMissions.length,
          completedAnalyses: acabades.length,
          inProgress: matrices.length - acabades.length
        });
      }

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard.goodMorning');
    if (hour < 17) return t('dashboard.goodAfternoon');
    return t('dashboard.goodEvening');
  };

  if (loading) {
    return (
      <div className="nordic-dashboard">
        <div className="nordic-loading">
          <div className="nordic-spinner"></div>
          <p>{t('dashboard.loadingDashboard')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="nordic-dashboard">
      {isInstructor ? (
        <InstructorDashboard
          user={user}
          stats={stats}
          missions={missions}
          greeting={getTimeBasedGreeting()}
          t={t}
        />
      ) : (
        <AnalystDashboard
          user={user}
          stats={stats}
          missions={missions}
          greeting={getTimeBasedGreeting()}
          t={t}
        />
      )}
    </div>
  );
}

function InstructorDashboard({ user, stats, missions, greeting, t }) {
  const { i18n } = useTranslation();
  const activeMissions = missions.filter(m => m.is_active);
  const inactiveMissions = missions.filter(m => !m.is_active && !m.archived);

  return (
    <>
      {/* Header */}
      <div className="nordic-header">
        <h1 className="nordic-greeting">{greeting}, {user?.display_name || user?.username}</h1>
        <p className="nordic-subtitle">{t('dashboard.courseManagementDashboard')}</p>
      </div>

      {/* Key Metrics - Only Real Data */}
      {stats && (
        <div className="nordic-stats-grid">
          <div className="nordic-stat-card">
            <div className="nordic-stat-value">{stats.activeMissions}</div>
            <div className="nordic-stat-label">{t('dashboard.activeMissions')}</div>
          </div>
          <div className="nordic-stat-card">
            <div className="nordic-stat-value">{stats.totalStudents}</div>
            <div className="nordic-stat-label">{t('dashboard.enrolledStudents')}</div>
          </div>
          <div className="nordic-stat-card">
            <div className="nordic-stat-value">{stats.totalMissions}</div>
            <div className="nordic-stat-label">{t('dashboard.totalMissions')}</div>
          </div>
          {stats.pendingApprovals > 0 && (
            <div className="nordic-stat-card">
              <div className="nordic-stat-value">{stats.pendingApprovals}</div>
              <div className="nordic-stat-label">{t('dashboard.pendingApprovals')}</div>
              <Link to="/users" className="nordic-btn nordic-btn-primary" style={{ marginTop: 'var(--space-md)', fontSize: '0.875rem' }}>
                {t('dashboard.review')}
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="nordic-main-grid">
        {/* Active Missions */}
        <div className="nordic-card">
          <div className="nordic-card-header">
            <h2 className="nordic-card-title">{t('dashboard.activeMissions')}</h2>
          </div>

          {activeMissions.length > 0 ? (
            <div className="nordic-mission-list">
              {activeMissions.map(mission => (
                <div key={mission.id} className="nordic-mission-item">
                  <div className="nordic-mission-content">
                    <h3 className="nordic-mission-title">{getLocalizedContent(mission.title, i18n.language)}</h3>
                    <div className="nordic-mission-meta">
                      <span>{mission.time_limit_minutes} {t('common.minutes')}</span>
                      {mission.assigned_analysts_count > 0 && (
                        <span>{mission.assigned_analysts_count} {t('dashboard.studentsAssigned')}</span>
                      )}
                    </div>
                  </div>
                  <div className="nordic-mission-actions">
                    <Link to={`/missions/${mission.id}/submissions`} className="nordic-btn nordic-btn-secondary">
                      {t('dashboard.viewSubmissions')}
                    </Link>
                    <Link to={`/missions/${mission.id}`} className="nordic-btn nordic-btn-ghost">
                      {t('dashboard.manage')}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="nordic-empty-state">
              <p>{t('dashboard.noActiveMissions')}</p>
              <Link to="/missions" className="nordic-btn nordic-btn-primary">
                {t('dashboard.createMission')}
              </Link>
            </div>
          )}
        </div>

        {/* Inactive Missions */}
        {inactiveMissions.length > 0 && (
          <div className="nordic-card">
            <div className="nordic-card-header">
              <h2 className="nordic-card-title">{t('dashboard.draftMissions')}</h2>
            </div>

            <div className="nordic-mission-list">
              {inactiveMissions.slice(0, 5).map(mission => (
                <div key={mission.id} className="nordic-mission-item">
                  <div className="nordic-mission-content">
                    <h3 className="nordic-mission-title">{getLocalizedContent(mission.title, i18n.language)}</h3>
                    <div className="nordic-mission-meta">
                      <span>{t('dashboard.notYetActivated')}</span>
                    </div>
                  </div>
                  <div className="nordic-mission-actions">
                    <Link to={`/missions/${mission.id}`} className="nordic-btn nordic-btn-secondary">
                      {t('dashboard.configure')}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="nordic-card">
          <div className="nordic-card-header">
            <h2 className="nordic-card-title">{t('dashboard.quickActions')}</h2>
          </div>
          <div className="nordic-action-grid">
            <Link to="/missions" className="nordic-action-item">
              <span className="nordic-action-title">{t('dashboard.createMission')}</span>
              <span className="nordic-action-desc">{t('dashboard.setUpNewACHExercise')}</span>
            </Link>
            <Link to="/users" className="nordic-action-item">
              <span className="nordic-action-title">{t('dashboard.manageUsers')}</span>
              <span className="nordic-action-desc">{t('dashboard.reviewStudentRegistrations')}</span>
            </Link>
            <Link to="/theory" className="nordic-action-item">
              <span className="nordic-action-title">{t('dashboard.achTheory')}</span>
              <span className="nordic-action-desc">{t('dashboard.reviewMethodologyGuide')}</span>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

function AnalystDashboard({ user, stats, missions, greeting, t }) {
  const { i18n } = useTranslation();
  const availableMissions = missions.filter(m => m.is_active && !m.archived);

  return (
    <>
      {/* Header */}
      <div className="nordic-header">
        <h1 className="nordic-greeting">{greeting}, {user?.display_name || user?.username}</h1>
        <p className="nordic-subtitle">{t('dashboard.analysisDashboard')}</p>
      </div>

      {/* Progress Overview - Only Real Data */}
      {stats && (
        <div className="nordic-progress-card">
          <div className="nordic-card-header">
            <h2 className="nordic-card-title">{t('dashboard.yourProgress')}</h2>
          </div>

          <div className="nordic-progress-stats">
            <div className="nordic-progress-item">
              <div className="nordic-progress-value">{stats.assignedMissions}</div>
              <div className="nordic-progress-label">{t('dashboard.assignedMissions')}</div>
            </div>
            <div className="nordic-progress-item">
              <div className="nordic-progress-value">{stats.completedAnalyses}</div>
              <div className="nordic-progress-label">{t('dashboard.completed')}</div>
            </div>
            <div className="nordic-progress-item">
              <div className="nordic-progress-value">{stats.inProgress}</div>
              <div className="nordic-progress-label">{t('dashboard.inProgress')}</div>
            </div>
          </div>
        </div>
      )}

      {/* Available Missions */}
      <div className="nordic-card">
        <div className="nordic-card-header">
          <h2 className="nordic-card-title">{t('dashboard.availableMissions')}</h2>
        </div>

        {availableMissions.length > 0 ? (
          <div className="nordic-mission-grid">
            {availableMissions.map(mission => (
              <div key={mission.id} className="nordic-mission-card-simple">
                <div className="nordic-mission-header">
                  <h3 className="nordic-mission-title">{getLocalizedContent(mission.title, i18n.language)}</h3>
                  <div className="nordic-mission-duration">{mission.time_limit_minutes} {t('common.minutes')}</div>
                </div>

                {getLocalizedContent(mission.description, i18n.language) && (
                  <p className="nordic-mission-description">
                    {(() => {
                      const desc = getLocalizedContent(mission.description, i18n.language);
                      return desc.length > 120 ? desc.substring(0, 120) + '...' : desc;
                    })()}
                  </p>
                )}

                <div className="nordic-mission-footer">
                  <div className="nordic-mission-badges">
                    {mission.hypotheses?.length > 0 && (
                      <span className="nordic-badge">{mission.hypotheses.length} {t('dashboard.hypotheses')}</span>
                    )}
                    {mission.evidence?.length > 0 && (
                      <span className="nordic-badge">{mission.evidence.length} {t('dashboard.evidence')}</span>
                    )}
                  </div>
                  <Link to={`/matrix/${mission.id}`} className="nordic-btn nordic-btn-primary">
                    {t('dashboard.startAnalysis')}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="nordic-empty-state">
            <p>{t('dashboard.noMissionsAvailable')}</p>
            <p className="nordic-empty-subtitle">{t('dashboard.checkBackSoon')}</p>
          </div>
        )}
      </div>

      {/* Learning Resources */}
      <div className="nordic-card">
        <div className="nordic-card-header">
          <h2 className="nordic-card-title">{t('dashboard.resources')}</h2>
        </div>

        <div className="nordic-resource-list">
          <Link to="/theory" className="nordic-resource-item">
            <span className="nordic-resource-title">{t('dashboard.achMethodology')}</span>
            <span className="nordic-resource-desc">{t('dashboard.completeGuideToAnalysisTechniques')}</span>
          </Link>
        </div>
      </div>
    </>
  );
}

export default Dashboard;